import type {
  IrrigationEquipmentKind,
  IrrigationProblemSubject,
  WaterSourceKind,
} from '../enums/irrigation.enum.js'
import {
  IRRIGATION_EQUIPMENT_KIND_LABELS,
  PROBLEM_SUBJECT_FOR_EQUIPMENT,
} from '../enums/irrigation.enum.js'
import type { IrrigationEntity } from '../input/irrigation.input.js'

/*
 * The rules a site's irrigation system has to satisfy as a whole: the ones a
 * single record's schema cannot check, because they are about how records
 * point at each other.
 *
 * One implementation, run at both ends. The server checks the state a sync
 * batch would leave and refuses the batch if this finds anything. The phone
 * checks the state an edit would leave and refuses the edit, because a batch
 * the server refuses is refused again on every retry and holds up everything
 * queued behind it. Two implementations would disagree eventually, and the
 * disagreement would be a stuck phone in a park.
 */

/** Just what the rules read of each record. */
export interface IrrigationRuleState {
  waterSources: readonly { id: string; kind: WaterSourceKind }[]
  timers: readonly { id: string; waterSourceId: string | null }[]
  zones: readonly { id: string; timerId: string; number: number }[]
  equipment: readonly {
    id: string
    kind: IrrigationEquipmentKind
    zoneId: string | null
    timerId: string | null
    waterSourceId: string | null
    /** The valve box it sits in. */
    boxId: string | null
  }[]
  problems: readonly {
    id: string
    typeId: string | null
    zoneId: string | null
    timerId: string | null
    equipmentId: string | null
    waterSourceId: string | null
    hasPosition: boolean
    /**
     * Open problems must be on something live. A fixed one is history: the
     * zone it was in can be retired without rewriting the record of what was
     * repaired there.
     */
    isOpen: boolean
  }[]
}

export interface IrrigationViolation {
  entity: IrrigationEntity
  id: string
  message: string
}

/**
 * What a problem is on, as a problem type names it, or null when what it
 * points at is not in the state at all.
 */
export function problemSubjectOf(
  problem: IrrigationRuleState['problems'][number],
  equipmentKindById: ReadonlyMap<string, IrrigationEquipmentKind>,
): IrrigationProblemSubject | null {
  if (problem.zoneId !== null) return problem.hasPosition ? 'ZONE_SPOT' : 'ZONE'
  if (problem.timerId !== null) return 'TIMER'
  if (problem.waterSourceId !== null) return 'WATER_SOURCE'
  if (problem.equipmentId !== null) {
    const kind = equipmentKindById.get(problem.equipmentId)
    return kind === undefined ? null : PROBLEM_SUBJECT_FOR_EQUIPMENT[kind]
  }
  return null
}

/**
 * Everything wrong with a site's irrigation state, or nothing.
 *
 * `problemTypes` maps each known type to what it applies to. Archived types
 * are included on purpose: archiving a type stops it being offered, it does
 * not make the problems already recorded with it invalid, and a phone that
 * picked it before it was archived must still be able to sync.
 */
export function findIrrigationViolations(
  state: IrrigationRuleState,
  problemTypes: ReadonlyMap<string, readonly IrrigationProblemSubject[]>,
): IrrigationViolation[] {
  const violations: IrrigationViolation[] = []
  const timerIds = new Set(state.timers.map((timer) => timer.id))
  const zoneIds = new Set(state.zones.map((zone) => zone.id))
  const sourceKindById = new Map(
    state.waterSources.map((source) => [source.id, source.kind]),
  )
  const equipmentKindById = new Map(
    state.equipment.map((item) => [item.id, item.kind]),
  )

  for (const timer of state.timers) {
    if (
      timer.waterSourceId !== null &&
      !sourceKindById.has(timer.waterSourceId)
    ) {
      violations.push({
        entity: 'timer',
        id: timer.id,
        message:
          'This timer would be left on a water source that is gone. Put it on another, or remove it too.',
      })
    }
  }

  const zoneNumbers = new Map<string, string>()
  for (const zone of state.zones) {
    if (!timerIds.has(zone.timerId)) {
      violations.push({
        entity: 'zone',
        id: zone.id,
        message: `Zone ${String(zone.number)} would be left without its timer. Move it to another timer, or remove it too.`,
      })
      continue
    }
    const key = `${zone.timerId}:${String(zone.number)}`
    if (zoneNumbers.has(key)) {
      violations.push({
        entity: 'zone',
        id: zone.id,
        message: `That timer already has a zone ${String(zone.number)}.`,
      })
    }
    zoneNumbers.set(key, zone.id)
  }

  const valveByZone = new Map<string, string>()
  for (const item of state.equipment) {
    if (item.zoneId !== null) {
      if (!zoneIds.has(item.zoneId)) {
        // Only a valve is put on a zone. Another kind that still points at
        // one should not be called a valve.
        const noun =
          item.kind === 'VALVE'
            ? 'valve'
            : IRRIGATION_EQUIPMENT_KIND_LABELS[item.kind].toLowerCase()
        violations.push({
          entity: 'equipment',
          id: item.id,
          message: `This ${noun} would be left on a zone that is gone. Put it on another zone, or remove it too.`,
        })
      } else if (item.kind === 'VALVE') {
        if (valveByZone.has(item.zoneId)) {
          violations.push({
            entity: 'equipment',
            id: item.id,
            message: 'That zone already has a valve. A zone has exactly one.',
          })
        }
        valveByZone.set(item.zoneId, item.id)
      }
    }
    if (item.timerId !== null && !timerIds.has(item.timerId)) {
      violations.push({
        entity: 'equipment',
        id: item.id,
        message:
          'This would be left on a timer that is gone. Put it on another timer, or remove it too.',
      })
    }
    if (item.boxId !== null) {
      const boxKind = equipmentKindById.get(item.boxId)
      if (boxKind === undefined) {
        violations.push({
          entity: 'equipment',
          id: item.id,
          message:
            'This would be left in a valve box that is gone. Take it out of the box, or remove it too.',
        })
      } else if (boxKind !== 'VALVE_BOX') {
        violations.push({
          entity: 'equipment',
          id: item.id,
          message: 'That is not a valve box.',
        })
      }
    }
    if (item.waterSourceId !== null) {
      const sourceKind = sourceKindById.get(item.waterSourceId)
      if (sourceKind === undefined) {
        violations.push({
          entity: 'equipment',
          id: item.id,
          message:
            'This would be left on a water source that is gone. Put it on another, or remove it too.',
        })
      } else if (item.kind === 'WELL_COMPONENT' && sourceKind !== 'WELL') {
        violations.push({
          entity: 'equipment',
          id: item.id,
          message: 'A well component belongs to a well, not a meter.',
        })
      }
    }
  }

  for (const problem of state.problems) {
    if (!problem.isOpen) continue
    const subject = problemSubjectOf(problem, equipmentKindById)
    const pointsAtLiveRecord =
      (problem.zoneId !== null && zoneIds.has(problem.zoneId)) ||
      (problem.timerId !== null && timerIds.has(problem.timerId)) ||
      (problem.equipmentId !== null &&
        equipmentKindById.has(problem.equipmentId)) ||
      (problem.waterSourceId !== null &&
        sourceKindById.has(problem.waterSourceId))
    if (subject === null || !pointsAtLiveRecord) {
      violations.push({
        entity: 'problem',
        id: problem.id,
        message:
          'This problem would be left on something that is gone. Remove the problem too.',
      })
      continue
    }
    if (problem.typeId === null) continue
    const appliesTo = problemTypes.get(problem.typeId)
    if (appliesTo === undefined) {
      violations.push({
        entity: 'problem',
        id: problem.id,
        message: 'That kind of problem is not in the directory.',
      })
    } else if (!appliesTo.includes(subject)) {
      violations.push({
        entity: 'problem',
        id: problem.id,
        message:
          subject === 'ZONE'
            ? 'That kind of problem is found at a spot in a zone. Pin where it is.'
            : 'That kind of problem is not recorded on this.',
      })
    }
  }

  return violations
}
